/** @format */

import PropTypes from "prop-types";
import {
	Avatar,
	Box,
	Button,
	Card,
	Grid,
	IconButton,
	Link,
	Stack,
	Typography,
} from "@mui/material";
import Iconify from "../iconify/Iconify";
import { fShortenNumber } from "../../utility/utils/format-number";
import { fDate } from "../../utility/utils/format-time";
import SvgColor from "../svg-color/SvgColor";
import { alpha } from "@mui/material/styles";
import { buttonColors, colors } from "../../utility/types/constant";
import { Configuration } from "../../utility/types/circleType";

interface Leader {
	_id?: string;
	firstName?: string;
	lastName?: string;
	email?: string;
	profileImage?: string;
}

interface Contribution {
	_id?: string;
	contributionAmount: string | number;
	contributionPeriod: string | number;
}

interface PayOut {
	_id?: string | number;
	payOutPeriod: string | number;
}

interface CircleCardProps {
	index?: any;
	_id?: string;
	circleName: string;
	circleImage?: any;
	description?: string;
	leader: Leader;
	contributions?: Contribution[];
	configuration: Configuration;
	payOuts: PayOut[];
	totalMembers: number;
	updatedAt?: any;
	members?: [];
	onClick?: (_id: string) => {};
	showJoinButton?: boolean;
	isMember?: boolean;
	onJoinButtonClick?: (_id: string) => void;
	onViewButtonClick?: (_id: string) => void;
	onMoreIconClick?: (_id: string) => void;
	joinButtonDisabled?: boolean;
	hasActiveCycle?: boolean;
	activeCycleInfo?: {
		startDate: string;
		endDate: string;
		payOutDate: string;
	} | null;
	inJoinWindow?: boolean;
	joinWindowMessage?: string;
	isPendingJoinRequest?: boolean;
}

const CircleCard = (props: CircleCardProps) => {
	const {
		index,
		_id,
		circleName,
		circleImage,
		// description,
		leader,
		// contributions,
		configuration,
		// payOuts,
		totalMembers,
		updatedAt,
		onClick,
		// showJoinButton,
		onJoinButtonClick,
		onViewButtonClick,
		onMoreIconClick,
		isMember,
		joinButtonDisabled = false,
		hasActiveCycle = false,
		activeCycleInfo = null,
		inJoinWindow = false,
		joinWindowMessage = "Join",
		isPendingJoinRequest = false,
	} = props;

	const latestCircleLarge = index === 0;

	const latestCircle = index > 0;

	const renderAvatar = (
		<Avatar
			alt={leader.firstName}
			src={leader.profileImage}
			sx={{
				zIndex: 9,
				width: 32,
				height: 32,
				position: "absolute",
				left: (theme) => theme.spacing(3),
				bottom: (theme) => theme.spacing(-2),
				...((latestCircleLarge || latestCircle) && {
					zIndex: 9,
					top: 24,
					left: 24,
					width: 40,
					height: 40,
				}),
			}}
		/>
	);

	const renderTitle = (
		<Link
			color="inherit"
			variant="subtitle2"
			underline="none"
			sx={{
				height: 44,
				overflow: "hidden",
				WebkitLineClamp: 2,
				display: "-webkit-box",
				WebkitBoxOrient: "vertical",
				...(latestCircleLarge && { typography: "h5", height: 60 }),
				...((latestCircleLarge || latestCircle) && {
					color: "common.white",
				}),
			}}>
			{circleName}
		</Link>
	);

	const renderInfo = (
		<Stack
			direction="row"
			flexWrap="wrap"
			spacing={1.9}
			justifyContent="flex-end"
			sx={{
				mt: 3,
				color: "text.disabled",
			}}>
			{[
				{ number: totalMembers, icon: "ic:round-groups-2" },
				{
					number: configuration.contributionAmount,
					icon: "streamline:subscription-cashflow-solid",
				},
				{
					string: configuration.contributionPeriod,
					icon: "uis:schedule",
				},
			].map((info, _index) => (
				<Stack
					key={_index}
					direction="row"
					sx={{
						...((latestCircleLarge || latestCircle) && {
							opacity: 2.59,
							color: "common.white",
						}),
					}}>
					<Iconify width={22} icon={info.icon} sx={{ mr: 0.5 }} />
					<Typography variant="caption">
						{fShortenNumber(info.number)}
					</Typography>
					<Typography variant="caption">{info.string}</Typography>
				</Stack>
			))}
		</Stack>
	);

	const renderCover = (
		<Box
			component="img"
			alt={circleImage}
			src={circleImage}
			sx={{
				top: 0,
				width: 1,
				height: 1,
				objectFit: "cover",
				position: "absolute",
			}}
		/>
	);

	const renderActiveCycleOverlay = hasActiveCycle &&
		!isMember &&
		!inJoinWindow && (
			<Box
				sx={{
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					textAlign: "center",
					backgroundColor: "rgba(211, 47, 47, 0.95)",
					color: "white",
					padding: { xs: 1.5, sm: 2 },
					borderRadius: 2,
					zIndex: 11,
					boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
					maxWidth: "90%",
				}}>
				<Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
					🔒 Cycle In Progress
				</Typography>
				<Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
					{joinWindowMessage}
				</Typography>
				{activeCycleInfo && (
					<Typography
						variant="caption"
						display="block"
						sx={{ fontSize: "0.65rem", opacity: 0.9 }}>
						Cycle ends: {new Date(activeCycleInfo.endDate).toLocaleDateString()}
					</Typography>
				)}
			</Box>
		);

	const renderJoinWindowOpenOverlay = hasActiveCycle &&
		!isMember &&
		inJoinWindow && (
			<Box
				sx={{
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					textAlign: "center",
					backgroundColor: "rgba(76, 175, 80, 0.95)",
					color: "white",
					padding: { xs: 1.5, sm: 2 },
					borderRadius: 2,
					zIndex: 11,
					boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
					maxWidth: "90%",
				}}>
				<Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
					✨ Join Window Open!
				</Typography>
				<Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
					{joinWindowMessage}
				</Typography>
				<Typography
					variant="caption"
					display="block"
					sx={{ fontSize: "0.65rem", opacity: 0.9 }}>
					Join now - Last 7 days of month
				</Typography>
			</Box>
		);

	const renderPendingRequestOverlay = isPendingJoinRequest && !isMember && (
		<Box
			sx={{
				position: "absolute",
				top: "50%",
				left: "50%",
				transform: "translate(-50%, -50%)",
				textAlign: "center",
				backgroundColor: "rgba(255, 152, 0, 0.95)",
				color: "white",
				padding: { xs: 1.5, sm: 2 },
				borderRadius: 2,
				zIndex: 11,
				boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
				maxWidth: "90%",
			}}>
			<Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
				⏳ Request Pending
			</Typography>
			<Typography variant="caption" display="block">
				Waiting for circle leader approval
			</Typography>
		</Box>
	);

	const renderDate = (
		<Typography
			variant="caption"
			component="div"
			sx={{
				mb: 2,
				color: "text.disabled",
				...((latestCircleLarge || latestCircle) && {
					opacity: 0.48,
					color: "common.white",
				}),
			}}>
			{fDate(updatedAt)}
		</Typography>
	);

	const renderShape = (
		<SvgColor
			color="paper"
			src=""
			sx={{
				width: 80,
				height: 36,
				zIndex: 9,
				bottom: -15,
				position: "absolute",
				color: "background.paper",
				...((latestCircleLarge || latestCircle) && { display: "none" }),
			}}
		/>
	);

	const renderActionButton = isMember ? (
		<Button
			onClick={() => onViewButtonClick && onViewButtonClick(_id ?? "")}
			variant="contained"
			size="small"
			sx={{
				position: "absolute",
				opacity: 1,
				boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2)",
				border: "1px solid",
				borderRadius: 25,
				backgroundColor: buttonColors.skyBlue,
				color: colors.white,
				"&:hover": {
					opacity: 1,
					backgroundColor: buttonColors.skyBlue,
					color: colors.white,
				},
				zIndex: 1,
				...(latestCircleLarge
					? {
							left: (theme) => theme.spacing(3),
							bottom: (theme) => theme.spacing(2),
					  }
					: latestCircle
					? {
							top: (theme) => theme.spacing(2),
							right: (theme) => theme.spacing(2),
					  }
					: {}),
			}}>
			View
		</Button>
	) : (
		<Button
			onClick={() => onJoinButtonClick && onJoinButtonClick(_id ?? "")}
			disabled={joinButtonDisabled}
			variant="contained"
			size="small"
			sx={{
				position: "absolute",
				opacity: 1,
				boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2)",
				border: "1px solid",
				borderRadius: 25,
				backgroundColor: buttonColors.primary,
				color: colors.white,
				"&:hover": {
					opacity: 1,
					backgroundColor: buttonColors.primary,
					color: colors.white,
				},
				zIndex: 1,
				...(latestCircleLarge
					? {
							left: (theme) => theme.spacing(3),
							bottom: (theme) => theme.spacing(2),
					  }
					: latestCircle
					? {
							top: (theme) => theme.spacing(2),
							right: (theme) => theme.spacing(2),
					  }
					: {}),
			}}>
			Join
		</Button>
	);

	const renderMoreIcon = latestCircleLarge ? (
		<IconButton
			onClick={() => onMoreIconClick && onMoreIconClick(_id ?? "")}
			sx={{
				position: "absolute",
				opacity: 2.59,
				color: "common.white",
				"&hover": {
					color: "common.white",
				},
				zIndex: 10,
				right: {
					xs: 24,
					sm: 24,
				},

				top: {
					xs: 24,
					sm: 24,
				},
			}}>
			<Avatar
				src="/assets/icons/animated-info-icon.gif"
				sx={{ width: 20, height: 20 }}
				alt="Info Icon"
			/>
		</IconButton>
	) : latestCircle ? (
		<IconButton
			onClick={() => onMoreIconClick && onMoreIconClick(_id ?? "")}
			sx={{
				position: "absolute",
				opacity: 2.59,
				color: "common.white",
				"&hover": {
					color: "common.white",
				},
				zIndex: 10,
				left: (theme) => theme.spacing(1),
				bottom: (theme) => theme.spacing(2),
			}}>
			<Avatar
				src="/assets/icons/animated-info-icon.gif"
				sx={{ width: 18, height: 18 }}
				alt="Info Icon"
			/>
		</IconButton>
	) : null;

	return (
		<Grid
			item
			xs={12}
			sm={latestCircleLarge ? 12 : 6}
			md={latestCircleLarge ? 6 : 3}>
			<Card onClick={() => onClick?.(_id ?? "")}>
				<Box
					sx={{
						position: "relative",
						pt: "calc(100% * 3 / 4)",
						...((latestCircleLarge || latestCircle) && {
							pt: "calc(100% * 4 / 3)",
							"&:after": {
								top: 0,
								content: "''",
								width: "100%",
								height: "100%",
								position: "absolute",
								bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
							},
						}),
						...(latestCircleLarge && {
							pt: {
								xs: "calc(100% * 4 / 3)",
								sm: "calc(100% * 3 / 4.66)",
							},
						}),
					}}>
					{renderShape}

					{renderAvatar}

					{renderCover}

					{/* Show overlays in priority order: pending > join window > cycle active */}
					{isPendingJoinRequest && renderPendingRequestOverlay}
					{!isPendingJoinRequest && inJoinWindow && renderJoinWindowOpenOverlay}
					{!isPendingJoinRequest && !inJoinWindow && renderActiveCycleOverlay}

					{/* {renderJoinButton} */}
					{renderActionButton}
					{renderMoreIcon}
				</Box>

				<Box
					sx={{
						p: (theme) => theme.spacing(4, 3, 3, 3),
						...((latestCircleLarge || latestCircle) && {
							width: 1,
							bottom: 0,
							position: "absolute",
						}),
					}}>
					{renderDate}

					{renderTitle}

					{renderInfo}
				</Box>
			</Card>
		</Grid>
	);
};

CircleCard.propTypes = {
	_id: PropTypes.string,
	circleName: PropTypes.string,
	circleImage: PropTypes.string,
	description: PropTypes.string,
	leader: PropTypes.object,
	contributions: PropTypes.any,
	configuration: PropTypes.any,
	payOuts: PropTypes.any,
	index: PropTypes.any,
	totalMembers: PropTypes.number,
	updatedAt: PropTypes.any,
	onClick: PropTypes.func,
	members: PropTypes.array,
	showJoinButton: PropTypes.bool,
	onMoreIconClick: PropTypes.func,
	isMember: PropTypes.bool,
	joinButtonDisabled: PropTypes.bool,
	hasActiveCycle: PropTypes.bool,
	activeCycleInfo: PropTypes.object,
	inJoinWindow: PropTypes.bool,
	joinWindowMessage: PropTypes.string,
	isPendingJoinRequest: PropTypes.bool,
};

export default CircleCard;
